import React, { useEffect, useState } from 'react';
import { PhotoUploadSection } from './PhotoUploadSection';
import { ProgressBar } from './ProgressBar';
import { SyrupSelection } from './SyrupSelection';
import { FormSection } from './FormSection';
import { CameraIcon } from 'lucide-react';
import axios from 'axios';
interface KRNWTRFormProps {
  customerId: string;
  onSubmit: () => void;
}
// Updated interface to match the webhook format
interface FormData {
  id: string;
  photos: {
    overview: string[];
    placement: string[];
    tap: string[];
    power: string[];
    drawings: string[];
  };
  type_werkblad: string;
  type_werkblad_overig: string;
  parkeerplaats: string;
  siropenBIBS: string[];
  siropenFlessen: Record<string, number>;
  siropenFlessenQuantity: number;
  overige_opmerkingen: string;
  skip_photos: boolean;
  photosNotAvailable: boolean;
  datum_ingevuld: string;
  bedrijfsnaam: string;
  emailadres: string;
  record?: string;
}
// Interface for the webhook payload format
interface WebhookPayload {
  id: string;
  type_werkblad: string;
  parkeerplaats: string;
  siropenBIBS: string[];
  siropenFlessen: any[];
  siropenFlessenQuantity: number;
  overige_opmerkingen: string;
  datum_ingevuld: string;
  skip_photos: boolean;
  record: string;
  bedrijfsnaam: string;
  emailadres: string;
  photos: {
    name: string;
    content: string;
  }[];
}
export function KRNWTRForm({
  customerId,
  onSubmit
}: KRNWTRFormProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    id: customerId,
    photos: {
      overview: [],
      placement: [],
      tap: [],
      power: [],
      drawings: []
    },
    type_werkblad: '',
    type_werkblad_overig: '',
    parkeerplaats: '',
    siropenBIBS: [],
    siropenFlessen: {},
    siropenFlessenQuantity: 0,
    overige_opmerkingen: '',
    skip_photos: false,
    photosNotAvailable: false,
    datum_ingevuld: new Date().toISOString().split('T')[0],
    bedrijfsnaam: '',
    emailadres: ''
  });
  // Calculate progress based on form completion
  useEffect(() => {
    let completed = 0;
    let total = 0;
    // Required fields
    if (formData.type_werkblad) completed++;
    if (formData.parkeerplaats) completed++;
    total += 2;
    // Required photos (if not marked as unavailable)
    if (!formData.photosNotAvailable) {
      if (formData.photos.overview.length > 0) completed++;
      if (formData.photos.placement.length > 0) completed++;
      if (formData.photos.tap.length > 0) completed++;
      if (formData.photos.power.length > 0) completed++;
      total += 3; // Only 3 required photos now (overview, tap, power)
    }
    const calculatedProgress = Math.floor(completed / total * 100);
    setProgress(calculatedProgress);
  }, [formData]);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    if (name === 'skip_photos') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'photosNotAvailable') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name.startsWith('bib-')) {
      const syrupName = name.replace('bib-', '');
      setFormData(prev => ({
        ...prev,
        siropenBIBS: checked ? [...prev.siropenBIBS, syrupName] : prev.siropenBIBS.filter(item => item !== syrupName)
      }));
    }
  };
  const handleBottleQuantityChange = (name: string, quantity: number) => {
    setFormData(prev => {
      // Calculate total quantity of all bottles
      const newFlessen = {
        ...prev.siropenFlessen,
        [name]: quantity
      };
      const totalQuantity = Object.values(newFlessen).reduce((sum, qty) => sum + qty, 0);
      return {
        ...prev,
        siropenFlessen: newFlessen,
        siropenFlessenQuantity: totalQuantity
      };
    });
  };
  const handlePhotoUpload = (section: keyof FormData['photos'], base64Images: string[]) => {
    setFormData(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [section]: base64Images
      }
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    // Validate required fields
    if (!formData.type_werkblad || !formData.parkeerplaats) {
      setError('Vul alle verplichte velden in');
      setIsSubmitting(false);
      return;
    }
    // Validate contact fields
    if (!formData.bedrijfsnaam || !formData.emailadres) {
      setError('Vul de contactgegevens in (bedrijfsnaam en e-mailadres)');
      setIsSubmitting(false);
      return;
    }
    // Validate photos if not marked as unavailable
    if (!formData.photosNotAvailable) {
      const requiredPhotoSections = ['overview', 'tap', 'power'] as const;
      for (const section of requiredPhotoSections) {
        if (formData.photos[section].length === 0) {
          setError(`Voeg a.u.b. een foto toe voor ${section}`);
          setIsSubmitting(false);
          return;
        }
      }
    }
    try {
      // Format data for the webhook according to the provided structure
      const webhookPayload: WebhookPayload = {
        id: formData.id,
        type_werkblad: formData.type_werkblad === 'Overig' ? `Overig: ${formData.type_werkblad_overig}` : formData.type_werkblad,
        parkeerplaats: formData.parkeerplaats,
        siropenBIBS: formData.siropenBIBS,
        siropenFlessen: [],
        siropenFlessenQuantity: formData.siropenFlessenQuantity,
        overige_opmerkingen: formData.overige_opmerkingen,
        datum_ingevuld: formData.datum_ingevuld,
        skip_photos: false,
        record: formData.id,
        bedrijfsnaam: formData.bedrijfsnaam,
        emailadres: formData.emailadres,
        photos: []
      };
      // Format photos for the webhook
      const photoSections = ['overview', 'placement', 'tap', 'power', 'drawings'] as const;
      photoSections.forEach(section => {
        formData.photos[section].forEach((base64Image, index) => {
          webhookPayload.photos.push({
            name: `${formData.id}-${section}-${index + 1}.jpg`,
            content: base64Image.split(',')[1] || base64Image // Remove data:image prefix if present
          });
        });
      });
      // Send data to the webhook
      const webhookUrl = 'https://hook.eu2.make.com/2xtvsajddcjm2zutmkx9ar3gg00dc3bt';
      await axios.post(webhookUrl, webhookPayload);
      onSubmit();
    } catch (err) {
      setError('Er is een fout opgetreden bij het verzenden van het formulier');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const nextStep = () => {
    // Validate required fields before proceeding to the next step
    if (currentStep === 1) {
      // Check if required fields are filled
      if (!formData.type_werkblad) {
        setError('Selecteer een type werkblad');
        return;
      }
      // If "Overig" is selected, make sure they specified what type
      if (formData.type_werkblad === 'Overig' && !formData.type_werkblad_overig) {
        setError('Specificeer het type werkblad bij "Overig"');
        return;
      }
      if (!formData.parkeerplaats) {
        setError('Vul de parkeerplaats informatie in');
        return;
      }
      // Clear any existing error
      setError('');
    }
    // Proceed to the next step
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  return <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 backdrop-blur-sm bg-opacity-70" data-id="element-53">
      <ProgressBar progress={progress} data-id="element-54" />
      <form onSubmit={handleSubmit} className="mt-6" data-id="element-55">
        {currentStep === 1 && <FormSection formData={formData} handleInputChange={handleInputChange} handleCheckboxChange={handleCheckboxChange} data-id="element-56" />}
        {currentStep === 2 && <SyrupSelection bibSyrups={formData.siropenBIBS} bottleSyrups={formData.siropenFlessen} handleCheckboxChange={handleCheckboxChange} handleBottleQuantityChange={handleBottleQuantityChange} onSkip={nextStep} data-id="element-57" />}
        {currentStep === 3 && <>
            <div className="mb-6" data-id="element-58">
              <div className="flex items-center justify-between" data-id="element-59">
                <div className="flex items-center gap-2" data-id="element-60">
                  <CameraIcon className="text-[#052858]" size={20} data-id="element-61" />
                  <h2 className="text-lg sm:text-xl font-semibold text-[#052858]" data-id="element-62">
                    Foto's uploaden
                  </h2>
                </div>
              </div>
              <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-63">
                <div className="flex items-center gap-3" data-id="element-64">
                  <input type="checkbox" id="photosNotAvailable" name="photosNotAvailable" checked={formData.photosNotAvailable} onChange={handleCheckboxChange} className="h-5 w-5 text-[#19EAC4] focus:ring-[#19EAC4] border-gray-300 rounded" data-id="element-65" />
                  <label htmlFor="photosNotAvailable" className="text-sm font-medium text-gray-700" data-id="element-66">
                    Locatiefoto's zijn nu niet beschikbaar (verbouwing/andere
                    reden)
                  </label>
                </div>
                {!formData.photosNotAvailable && <p className="text-sm text-gray-500 mt-3 ml-8" data-id="element-67">
                    Upload de vereiste foto's van de locatie
                  </p>}
                {formData.photosNotAvailable && <div className="mt-3 ml-8 p-3 bg-amber-50 border border-amber-200 rounded-md" data-id="element-68">
                    <p className="text-sm text-amber-700 flex items-center gap-2" data-id="element-69">
                      <span className="font-bold" data-id="element-70">💡 Tip:</span> Heb je
                      bouwtekeningen of plattegronden? Die helpen ons ook goed
                      bij de planning!
                    </p>
                  </div>}
              </div>
            </div>
            {!formData.photosNotAvailable ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-71">
                <PhotoUploadSection title="Overzicht" description="Foto van de gehele ruimte" section="overview" onUpload={images => handlePhotoUpload('overview', images)} required={true} data-id="element-72" />
                <PhotoUploadSection title="Plaatsing koeler" description="Foto van de locatie koeler" section="placement" onUpload={images => handlePhotoUpload('placement', images)} required={false} data-id="element-73" />
                <PhotoUploadSection title="Wasmachinekraan" description="Foto van de wasmachinekraan" section="tap" onUpload={images => handlePhotoUpload('tap', images)} required={true} data-id="element-74" />
                <PhotoUploadSection title="Stroom" description="Foto van de stroomaansluiting" section="power" onUpload={images => handlePhotoUpload('power', images)} required={true} data-id="element-75" />
                <PhotoUploadSection title="Bouwkundige tekeningen" description="Optionele tekeningen" section="drawings" onUpload={images => handlePhotoUpload('drawings', images)} required={false} data-id="element-76" />
              </div> : <div className="grid grid-cols-1 gap-4" data-id="element-77">
                <PhotoUploadSection title="Bouwkundige tekeningen" description="Upload hier bouwtekeningen of plattegronden (optioneel)" section="drawings" onUpload={images => handlePhotoUpload('drawings', images)} required={false} data-id="element-78" />
              </div>}
          </>}
        {currentStep === 4 && <div className="space-y-4" data-id="element-79">
            <h2 className="text-lg sm:text-xl font-semibold text-[#052858]" data-id="element-80">
              Contactgegevens
            </h2>
            <div data-id="element-81">
              <label htmlFor="bedrijfsnaam" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-82">
                Bedrijfsnaam <span className="text-red-500" data-id="element-83">*</span>
              </label>
              <input type="text" id="bedrijfsnaam" name="bedrijfsnaam" value={formData.bedrijfsnaam} onChange={handleInputChange} className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-[#19EAC4] bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#19EAC4] shadow-sm text-sm sm:text-base" placeholder="Naam van uw bedrijf" required data-id="element-84" />
            </div>
            <div data-id="element-85">
              <label htmlFor="emailadres" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-86">
                E-mailadres <span className="text-red-500" data-id="element-87">*</span>
              </label>
              <input type="email" id="emailadres" name="emailadres" value={formData.emailadres} onChange={handleInputChange} className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-[#19EAC4] bg-white rounded-md focus:outline-none focus:ring-2 focus:ring-[#19EAC4] shadow-sm text-sm sm:text-base" placeholder="uw@email.nl" required data-id="element-88" />
            </div>
          </div>}
        {error && <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md" data-id="element-89">
            <p className="text-red-600 text-sm" data-id="element-90">{error}</p>
          </div>}
        <div className="mt-6 flex justify-between" data-id="element-91">
          {currentStep > 1 && <button type="button" onClick={prevStep} className="px-3 py-2 sm:px-4 sm:py-2 border border-[#052858] text-[#052858] rounded-md hover:bg-gray-50 text-sm sm:text-base" data-id="element-92">
              Vorige
            </button>}
          {currentStep < 4 ? <button id="next-button" type="button" onClick={nextStep} className="ml-auto px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-[#19EAC4] to-[#052858] text-white rounded-md hover:opacity-90 transition-opacity text-sm sm:text-base" data-id="element-93">
              Volgende
            </button> : <button type="submit" disabled={isSubmitting} className="ml-auto px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-[#19EAC4] to-[#052858] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 text-sm sm:text-base" data-id="element-94">
              {isSubmitting ? 'Bezig met verzenden...' : 'Verzenden'}
            </button>}
        </div>
      </form>
    </div>;
}